import BankApplicationStatusLabel from '@/components/BankApplicationStatusLabel';
import { SVLink, SVSummaryCard } from '@/components/core';
import ExternalAccountStatusLabel from '@/components/ExternalAccountStatusLabel';
import { ROUTES } from '@/constants/routes';
import { BankApplicationDetails } from '@/types/bankApplications';
import { SVSummaryCardItem } from '@/types/common';
import {
  getBankApplicationAccountTypeLabel,
  getBankApplicationSubmissionTypeLabel,
} from '@/utils/bankAccountApplications';
import { formatCurrency } from '@/utils/currency';
import { formatDateTimeFromUTC } from '@/utils/date';
import { Box, Stack, Typography } from '@mui/material';

type BankApplicationSubmissionDetailsProps = {
  bankApplication: BankApplicationDetails;
};

export default function BankApplicationSubmissionDetails({
  bankApplication,
}: BankApplicationSubmissionDetailsProps) {
  const {
    applicationId,
    createdAt,
    createdBy,
    submittedAt,
    submittedBy,
    deletedAt,
    deletedBy,
    updatedAt,
    status,
    submissionType,
    owners,
    accountType,
    advisor,
    beneficiaries,
    depositAmount,
    withdrawFrom,
    externalAccounts,
  } = bankApplication;

  const signedAtList = owners.filter(
    owner => typeof owner.signedAt === 'string',
  );

  const items: SVSummaryCardItem[] = [
    {
      label: 'Application Id',
      text: applicationId,
    },
    {
      label: 'Created At',
      text: formatDateTimeFromUTC(createdAt, true),
    },
    {
      label: 'Created By',
      text: createdBy?.fullName,
    },
    {
      label: 'Submitted At',
      text: formatDateTimeFromUTC(submittedAt, true),
    },
    {
      label: 'Submitted By',
      text: submittedBy
        ? `${submittedBy.firstName} ${submittedBy.lastName} ${submittedBy.email}`
        : '',
    },
    {
      label: 'Signed At',
      customContent: externalAccounts?.length ? (
        <Stack spacing={1}>
          {signedAtList.map(owner => (
            <Typography key={`signedAt${owner.consumer.uuid}`}>
              {owner.consumer.fullName} -{' '}
              {formatDateTimeFromUTC(owner.signedAt)}
            </Typography>
          ))}
        </Stack>
      ) : null,
    },
    {
      label: 'Deleted At',
      text: formatDateTimeFromUTC(deletedAt, true),
    },
    {
      label: 'Deleted By',
      text: deletedBy ? `${deletedBy.firstName} ${deletedBy.lastName}` : '',
    },
    {
      label: 'Updated At',
      text: formatDateTimeFromUTC(updatedAt, true),
    },
    {
      label: 'Status',
      customContent: <BankApplicationStatusLabel status={status} />,
    },
    {
      label: 'Submission Type',
      text: getBankApplicationSubmissionTypeLabel(submissionType),
    },
    {
      label: 'Account Type',
      text: getBankApplicationAccountTypeLabel(accountType),
    },
    {
      label: 'Owners',
      customContent: (
        <Stack spacing={1}>
          {owners.map(owner => (
            <SVLink
              key={`owner${owner.consumer.uuid}`}
              to={`${ROUTES.CUSTOMERS}/${owner.consumer.uuid}`}>
              {owner.consumer.fullName}
            </SVLink>
          ))}
        </Stack>
      ),
    },
    {
      label: 'Advisor',
      customContent: (
        <SVLink
          key={`advisor${advisor.uuid}`}
          to={`${ROUTES.TEAMMATES}/${advisor.uuid}`}>
          {advisor.firstName} {advisor.lastName}
        </SVLink>
      ),
    },
    {
      label: 'Beneficiaries',
      customContent: beneficiaries.length ? (
        <Stack spacing={1}>
          {beneficiaries.map(beneficiary => (
            <Typography key={beneficiary.tin}>
              {beneficiary.firstName} {beneficiary.lastName} -{' '}
              {beneficiary.percentOfAllocation}%
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography>N/A</Typography>
      ),
    },
    {
      label: 'External Accounts',
      customContent: externalAccounts?.length ? (
        <Stack spacing={1}>
          {externalAccounts?.map(externalAccount => (
            <Box
              key={externalAccount.uuid}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
              <Typography>{externalAccount.description}</Typography>
              <ExternalAccountStatusLabel status={externalAccount.status} />
            </Box>
          ))}
        </Stack>
      ) : null,
    },
    {
      label: 'Initial Deposit',
      text: depositAmount
        ? `${formatCurrency({ amount: depositAmount.value })} from ${withdrawFrom?.description}`
        : '',
    },
  ];

  return <SVSummaryCard label="Submission Details" items={items} />;
}
